import React, { useState, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

interface AudioButtonProps {
  word: string;
  className?: string;
}

export const AudioButton: React.FC<AudioButtonProps> = ({ word, className = '' }) => {
  const [speaking, setSpeaking] = useState(false);
  const [available] = useState(() => 'speechSynthesis' in window);

  const handleSpeak = useCallback(() => {
    if (!available) return;

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(word);
    utterance.lang = 'en-US';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utterance);
  }, [word, available]);

  if (!available) {
    return (
      <button
        disabled
        className={`inline-flex items-center justify-center text-text-dim cursor-not-allowed ${className}`}
        aria-label="Audio not available"
      >
        <VolumeX size={18} />
      </button>
    );
  }

  return (
    <button
      onClick={handleSpeak}
      className={`inline-flex items-center justify-center transition-colors duration-150 ${
        speaking
          ? 'text-gold'
          : 'text-text-dim hover:text-gold-text'
      } ${className}`}
      aria-label={`Pronounce ${word}`}
      aria-pressed={speaking}
    >
      <Volume2 size={18} className={speaking ? 'animate-pulse' : ''} />
    </button>
  );
};
